import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
} from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { useState, useEffect } from 'react';
import TypesafeI18n from '@/i18n/i18n-react';
import { loadLocaleAsync } from '@/i18n/i18n-util.async';
import { detectUserLocale } from '@/lib/locale-utils';
import type { Locales } from '@/i18n/i18n-types';
import { LoginPage } from './pages/login.page';
import { CreateAccountPage } from './pages/create-account.page';
import { AuthRouteTransition } from './pages/auth/auth-route-transition';
import LoginSuccessPage from './pages/login-success.page';
import SelectTenantPage from './pages/select-tenant.page';
import DashboardPage from './pages/dashboard.page';
import ListCampaignsPage from './pages/list-campaigns.page';
import CampaignImportBatchPage from './pages/campaign-import-batch.page';
import CreateCampaignPage from './pages/create-campaign.page';
import CreateCampaignV2Page from './pages/create-campaign-v2.page';
import CampaignDetailsPage from './pages/campaign-details.page';
import CampaignImportPage from './pages/campaign-import.page';
import { TenantGuard, TenantProvider } from './hooks/use-tenant';
import { SelfGuard, SelfProvider } from './hooks/use-self';
import { TenantCreationRequestProvider } from './hooks/use-tenant-creation-request';
import { SignupGuard } from './pages/signup/signup-guard';
import * as onboardingGuards from './pages/onboarding/onboarding-guard-rules';
import ChooseStorePage from './pages/onboarding/1-choose-store.page';
import AppNameAndLinkPage from './pages/onboarding/2-app-name-and-link.page';
import AppLogoPage from './pages/onboarding/3-app-logo.page';
import TabBarConfigPage from './pages/onboarding/4-tab-bar-config.page';
import NotificationPreviewPage from './pages/onboarding/5-notification-preview.page';
import OnboardingCompletePage from './pages/onboarding/6-onboarding-complete.page';
import CongratulationsPage from './pages/congratulations.page';
import { SalesRedirect } from './pages/sales-redirect';
import { NuvemshopIntegrationSetupPage } from './pages/nuvemshop-integration-setup.page';
import { ShopifyIntegrationSetupPage } from './pages/shopify-integration-setup.page';
import AccountDeletionPage from './pages/account-deletion.page';
import { queryClient } from './lib/query-client';
import { LocalSettingsProvider } from './hooks/use-local-settings';
import AppCustomizationPage from './pages/app-customization.page';
import BannerConfigPage from './pages/banner-config.page';
import WelcomeNotificationPage from './pages/welcome-notification.page';
import WebsiteBannerPage from './pages/website-banner.page';
import WebsiteBannerSetupPage from './pages/website-banner-setup.page';
import { ShopifyAppPage } from './pages/shopify-app.page';
import SettingsPage from './pages/settings.page';
import TeamPage from './pages/team.page';
import BillingPage from './pages/billing.page';
import MetricsPage from './pages/metrics.page';
import AudiencesIndexPage from './pages/audiences-index.page';
import AudienceDetailsPage from './pages/audience-details.page';
import { RequireIntegrationLayout } from './layouts/require-integration.layout';
import { RequireAudiencesFeatureLayout } from './layouts/require-audiences-feature.layout';
import { GlobalChartGradients } from './lib/chart-gradients';
import { BillingLockModal } from './components/billing-lock-modal';
import { WhatsNewProvider } from './components/whats-new/whats-new-provider';
import { PastDueLockModal } from './components/past-due-lock-modal';
import { CustomPaymentLinkModal } from './components/custom-payment-link-modal';
import { IntegrationUpdateModal } from './components/integration-update-modal';
import { ChatwootWidget } from './components/chatwoot-widget/chatwoot-widget';
import { ChatwootWidgetProvider } from './components/chatwoot-widget/chatwoot-widget-context';
import { HelpFab } from './components/chatwoot-widget/help-fab';
import { SidebarProvider } from './components/ui/sidebar';
import { Toaster } from './components/ui/sonner';
import CreateAudiencePage from './pages/create-audience.page';
import CreateAudienceSimplifiedPage from './pages/create-audience-simplified.page';
import EditAudiencePage from './pages/edit-audience.page';
import EditAudienceSimplifiedPage from './pages/edit-audience-simplified.page';
import TreeDndSandboxPage from './pages/tree-dnd-sandbox.page';
import IncidentPage from './pages/incident.page';
import WhatsNewPage from './pages/whats-new.page';

const router = createBrowserRouter([
  {
    element: (
      <NuqsAdapter>
        <ScrollRestoration />
        <SidebarProvider>
          <SelfProvider>
            <TenantProvider>
              <ChatwootWidgetProvider>
                <Outlet />
                <Toaster />
                <ChatwootWidget />
                <HelpFab />
              </ChatwootWidgetProvider>
            </TenantProvider>
          </SelfProvider>
        </SidebarProvider>
      </NuqsAdapter>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/login" />,
      },
      {
        path: '/congratulations',
        element: <CongratulationsPage />,
      },
      {
        element: <AuthRouteTransition />,
        children: [
          {
            path: '/login',
            element: <LoginPage />,
          },
          {
            path: '/create-account',
            element: <CreateAccountPage />,
          },
          {
            path: '/login/success',
            element: <LoginSuccessPage />,
          },
        ],
      },

      {
        element: (
          <SelfGuard>
            <LocalSettingsProvider>
              <Outlet />
            </LocalSettingsProvider>
          </SelfGuard>
        ),
        children: [
          {
            path: '/onboarding/:tenantCreationRequestSlug',
            element: (
              <TenantCreationRequestProvider>
                <Outlet />
              </TenantCreationRequestProvider>
            ),
            children: [
              {
                path: '',
                element: <Navigate to="choose-store" />,
              },
              {
                path: 'choose-store',
                element: (
                  <SignupGuard
                    rules={[onboardingGuards.ruleStageIsWaitingForInfo]}
                  >
                    <ChooseStorePage />
                  </SignupGuard>
                ),
              },
              {
                path: 'app-name-link',
                element: (
                  <SignupGuard
                    rules={[
                      onboardingGuards.ruleStageIsWaitingForInfo,
                      onboardingGuards.ruleHasStorePlatform,
                    ]}
                  >
                    <AppNameAndLinkPage />
                  </SignupGuard>
                ),
              },
              {
                path: 'app-logo',
                element: (
                  <SignupGuard
                    rules={[
                      onboardingGuards.ruleStageIsWaitingForInfo,
                      onboardingGuards.ruleHasStorePlatform,
                      onboardingGuards.ruleHasAppIdentity,
                    ]}
                  >
                    <AppLogoPage />
                  </SignupGuard>
                ),
              },
              {
                path: 'tab-bar',
                element: (
                  <SignupGuard
                    rules={[
                      onboardingGuards.ruleStageIsWaitingForInfo,
                      onboardingGuards.ruleHasStorePlatform,
                      onboardingGuards.ruleHasAppIdentity,
                      onboardingGuards.ruleHasAppLogo,
                    ]}
                  >
                    <TabBarConfigPage />
                  </SignupGuard>
                ),
              },
              {
                path: 'notification',
                element: (
                  <SignupGuard
                    rules={[
                      onboardingGuards.ruleStageIsWaitingForInfo,
                      onboardingGuards.ruleHasStorePlatform,
                      onboardingGuards.ruleHasAppIdentity,
                      onboardingGuards.ruleHasAppLogo,
                      onboardingGuards.ruleHasAtLeastOneTab,
                    ]}
                  >
                    <NotificationPreviewPage />
                  </SignupGuard>
                ),
              },
              {
                path: 'complete',
                element: (
                  <SignupGuard
                    rules={[
                      onboardingGuards.ruleHasStorePlatform,
                      onboardingGuards.ruleHasAppIdentity,
                      onboardingGuards.ruleHasAppLogo,
                      onboardingGuards.ruleHasAtLeastOneTab,
                    ]}
                  >
                    <OnboardingCompletePage />
                  </SignupGuard>
                ),
              },
            ],
          },
          {
            path: '/select-tenant',
            element: <SelectTenantPage />,
          },
          {
            path: '/:tenantSlug',
            element: (
              <TenantGuard>
                <WhatsNewProvider>
                  <Outlet />
                  <BillingLockModal />
                  <PastDueLockModal />
                  <CustomPaymentLinkModal />
                  <IntegrationUpdateModal />
                </WhatsNewProvider>
              </TenantGuard>
            ),
            children: [
              {
                path: '',
                element: <Navigate to="dashboard" />,
              },
              {
                path: 'dashboard',
                element: <DashboardPage />,
              },
              {
                path: 'metrics',
                element: <MetricsPage />,
              },
              {
                path: 'sales',
                element: <SalesRedirect />,
              },
              {
                path: 'campaigns',
                children: [
                  {
                    path: '',
                    element: <ListCampaignsPage />,
                  },
                  {
                    path: 'create',
                    element: <CreateCampaignPage />,
                  },
                  {
                    path: 'create-v2',
                    element: <CreateCampaignV2Page />,
                  },
                  {
                    path: 'import',
                    element: <CampaignImportPage />,
                  },
                  {
                    path: 'import/:batchId',
                    element: <CampaignImportBatchPage />,
                  },
                  {
                    path: ':id',
                    element: <CampaignDetailsPage />,
                  },
                ],
              },
              {
                path: 'audiences',
                children: [
                  {
                    path: '',
                    element: <AudiencesIndexPage />,
                  },
                  {
                    element: <RequireAudiencesFeatureLayout />,
                    children: [
                      {
                        path: 'create-advanced',
                        element: <CreateAudiencePage />,
                      },
                      {
                        path: 'create',
                        element: <CreateAudienceSimplifiedPage />,
                      },
                      {
                        path: ':audienceId/edit',
                        element: <EditAudienceSimplifiedPage />,
                      },
                      {
                        path: ':audienceId/edit-advanced',
                        element: <EditAudiencePage />,
                      },
                      {
                        path: ':audienceId',
                        element: <AudienceDetailsPage />,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'dev/tree-dnd',
                element: <TreeDndSandboxPage />,
              },
              {
                path: 'customization',
                children: [
                  {
                    path: '',
                    element: <AppCustomizationPage />,
                  },
                  {
                    path: 'coupon-banner',
                    element: <BannerConfigPage />,
                  },
                  {
                    path: 'welcome-notification',
                    element: <WelcomeNotificationPage />,
                  },
                  {
                    path: 'website-banner',
                    element: (
                      <RequireIntegrationLayout>
                        <Outlet />
                      </RequireIntegrationLayout>
                    ),
                    children: [
                      { path: '', element: <WebsiteBannerPage /> },
                      { path: 'setup', element: <WebsiteBannerSetupPage /> },
                    ],
                  },
                ],
              },
              {
                path: 'settings',
                children: [
                  {
                    path: '',
                    element: <SettingsPage />,
                  },
                ],
              },
              {
                path: 'team',
                element: <TeamPage />,
              },
              {
                path: 'billing',
                element: <BillingPage />,
              },
              {
                path: 'whats-new',
                element: <WhatsNewPage />,
              },
              {
                path: 'incidents/:incidentSlug',
                element: <IncidentPage />,
              },
              // {
              //   path: 'help',
              //   element: <HelpPage />,
              // },
              {
                path: 'integrations/nuvemshop/setup',
                element: <NuvemshopIntegrationSetupPage />,
              },
              {
                path: 'integrations/shopify/setup',
                element: <ShopifyIntegrationSetupPage />,
              },
            ],
          },
        ],
      },
      {
        path: '/shopify-app',
        element: <ShopifyAppPage />,
      },
      {
        path: '/account-deletion/:tenantSlug',
        element: <AccountDeletionPage />,
      },
    ],
  },
]);

function App() {
  const [locale, setLocale] = useState<Locales>('en');
  const [localesLoaded, setLocalesLoaded] = useState(false);

  useEffect(() => {
    const detectedLocale = detectUserLocale();
    setLocale(detectedLocale);
    loadLocaleAsync(detectedLocale).then(() => setLocalesLoaded(true));
  }, []);

  if (!localesLoaded) {
    return null; // or a loading spinner
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TypesafeI18n locale={locale}>
        <GlobalChartGradients />
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
        <RouterProvider router={router} />
      </TypesafeI18n>
    </QueryClientProvider>
  );
}

export default App;
